import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ChefHat, Clock, Smartphone, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const LandingPage = () => {
  const features = [
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Order from Your Phone",
      description: "Skip the wait. Order fresh cuts right from your device — no payment needed upfront."
    },
    {
      icon: <ChefHat className="w-8 h-8" />,
      title: "Expert Preparation",
      description: "Our skilled butchers prepare your order fresh while you shop."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Pickup & Pay",
      description: "Get notified when ready, pick up in-store, and pay at the counter."
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F2]">
      {/* Top Bar with Contact */}
      <div className="bg-[#1A1A1A] text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between text-sm">
          <span>4817 W Oakton, Skokie, IL 60077</span>
          <div className="flex items-center gap-4">
            <Link to="/timecard" className="text-[#666] hover:text-white text-xs">
              Master
            </Link>
            <a href="tel:8476520682" className="flex items-center gap-1 hover:text-[#A61B1B]">
              <Phone className="w-4 h-4" />
              847-652-0682
            </a>
          </div>
        </div>
      </div>

      {/* Header with Logos */}
      <header className="bg-white border-b-2 border-[#DCD7CC] py-4">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          {/* Market Place Logo */}
          <img 
            src="https://www.marketplaceonoakton.com/images/market-place-on-oakton.png" 
            alt="Market Place on Oakton" 
            className="h-16 md:h-20 w-auto"
            data-testid="marketplace-logo"
          />
          
          {/* HMS Logo */}
          <div className="flex items-center gap-3">
            <img 
              src="https://www.hmsusa.org/img/logo.png" 
              alt="HMS Certified Halal" 
              className="h-20 md:h-28 w-auto"
              data-testid="hms-logo"
            />
            <div className="hidden md:block text-right">
              <p className="text-sm font-bold text-[#1A5E1F] uppercase">HMS Certified</p>
              <p className="text-sm text-[#8A8A8A]">Zabiha Halal</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section relative min-h-[70vh]" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1690983320937-ca293f1d1d97?w=1600&q=80')` 
          }}
        />
        <div className="hero-overlay absolute inset-0 bg-gradient-to-r from-[#1A1A1A]/95 via-[#1A1A1A]/80 to-[#1A1A1A]/40" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="max-w-2xl">
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="inline-block bg-[#1A5E1F] text-white px-4 py-2 animate-fade-in">
                <span className="font-bold">ZABIHA HALAL MEATS</span>
              </div>
              <div className="inline-block bg-[#A61B1B] text-white px-4 py-2 animate-fade-in">
                <span className="font-bold">IN-STORE PICKUP ONLY</span>
              </div>
              <div className="inline-block bg-[#F59E0B] text-black px-4 py-2 animate-fade-in">
                <span className="font-bold">NO PAYMENT REQUIRED ONLINE</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-[800] text-white tracking-tight leading-none mb-6 animate-fade-in stagger-1" style={{ fontFamily: 'Fraunces, serif' }}>
              Meat Department<br />
              <span className="text-[#A61B1B]">Order Ahead</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed mb-4 animate-fade-in stagger-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Skip the line. Order fresh Zabiha Halal cuts from your phone, pick up in-store, and pay when you collect. Simple and easy!
            </p>
            
            {/* Easy 2-Step Process */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg px-5 py-4 mb-8 animate-fade-in stagger-2">
              <p className="text-white font-bold text-lg mb-2">Easy 2-Step Process:</p>
              <div className="flex flex-col sm:flex-row gap-4 text-gray-200">
                <div className="flex items-center gap-2">
                  <span className="bg-[#A61B1B] text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm">1</span>
                  <span>Select your meats & quantities</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="bg-[#A61B1B] text-white w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm">2</span>
                  <span>Pick up & pay in-store</span>
                </div>
              </div>
              <p className="text-[#4ADE80] font-medium mt-3 text-sm">✓ No online payment required • Ready when you arrive</p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
              <Link to="/order">
                <Button 
                  data-testid="order-now-btn"
                  className="bg-[#A61B1B] text-white hover:bg-[#8B1515] rounded-sm px-8 py-6 text-lg font-bold tracking-wide uppercase shadow-lg transition-transform hover:-translate-y-1"
                >
                  Order Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/track">
                <Button 
                  data-testid="track-order-btn"
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#1A1A1A] rounded-sm px-8 py-6 text-lg font-bold uppercase transition-colors bg-transparent"
                >
                  Track Order
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* HMS Certification Section - Big & Prominent */}
      <section className="py-12 bg-white" data-testid="certification-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-4xl font-[700] text-[#1A1A1A] mb-2" style={{ fontFamily: 'Fraunces, serif' }}>
              100% Zabiha Halal Certified
            </h2>
            <p className="text-[#8A8A8A]">Hand slaughtered according to Islamic guidelines</p>
          </div>
          
          <div className="flex flex-col md:flex-row items-center justify-center gap-12">
            {/* HMS Logo Big */}
            <div className="text-center">
              <img 
                src="https://www.hmsusa.org/img/logo.png" 
                alt="HMS - Halal Monitoring Services" 
                className="h-48 md:h-64 lg:h-80 w-auto mx-auto mb-6"
                data-testid="hms-logo-big"
              />
              <p className="text-xl md:text-2xl font-bold text-[#1A5E1F]">Halal Monitoring Services</p>
              <p className="text-base text-[#8A8A8A]">Trusted Certification Since 1995</p>
            </div>
            
            {/* Certificate */}
            <div 
              className="bg-[#F9F7F2] p-4 border-4 border-[#1A5E1F] rounded-lg cursor-pointer hover:shadow-xl transition-shadow"
              onClick={() => window.open('https://customer-assets.emergentagent.com/job_meatorder-pro/artifacts/p7pkvi8n_image.png', '_blank')}
              data-testid="certificate-display"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_meatorder-pro/artifacts/p7pkvi8n_image.png" 
                alt="HMS Zabiha Halal Certificate - Market Place on Oakton" 
                className="h-48 md:h-64 w-auto rounded"
              />
              <p className="text-center text-sm text-[#1A5E1F] font-bold mt-3">Click to view full certificate</p>
            </div>
          </div>
          
          <div className="mt-8 text-center">
            <div className="inline-flex items-center gap-2 bg-[#E6F4EA] text-[#1A5E1F] px-6 py-3 rounded-full">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="font-medium">Verified HMS Certified Establishment</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto" data-testid="features-section">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-4xl font-[700] text-[#1A1A1A] tracking-tight mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
            How It Works
          </h2>
          <p className="text-[#8A8A8A] text-lg max-w-2xl mx-auto">
            A better way to shop for fresh Halal meat
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="bg-white border border-[#DCD7CC] p-8 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`feature-card-${index}`}
            >
              <div className="w-16 h-16 bg-[#A61B1B] text-white rounded-sm flex items-center justify-center mb-6">
                {feature.icon}
              </div>
              <h3 className="text-xl font-[600] text-[#1A1A1A] mb-3" style={{ fontFamily: 'Fraunces, serif' }}>
                {feature.title}
              </h3>
              <p className="text-[#8A8A8A] leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* QR Code Section */}
      <section className="py-16 bg-[#1A1A1A]" data-testid="qr-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="text-center md:text-left max-w-xl">
              <h2 className="text-2xl md:text-4xl font-[700] text-white tracking-tight mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
                Scan to Order
              </h2>
              <p className="text-gray-400 text-lg mb-6">
                Use your phone's camera to scan this QR code and start your order instantly.
              </p>
              <p className="text-[#A61B1B] font-bold text-lg">
                www.ordermeatonoakton.com
              </p>
            </div>
            
            <div className="qr-container bg-white p-8 rounded-sm shadow-2xl" data-testid="qr-code">
              <QRCodeSVG 
                value="https://www.ordermeatonoakton.com/order"
                size={180}
                level="H"
                includeMargin={true}
                fgColor="#1A1A1A"
                bgColor="#FFFFFF"
              />
              <p className="text-center text-sm text-[#8A8A8A] mt-4 font-medium">
                Scan with your camera
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-white border-t border-[#DCD7CC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-4">
              <img 
                src="https://www.marketplaceonoakton.com/images/market-place-on-oakton.png" 
                alt="Market Place on Oakton" 
                className="h-10 w-auto"
              />
              <div className="text-sm text-[#8A8A8A]">
                <p>4817 W Oakton, Skokie, IL 60077</p>
                <p>Mon-Sun: 8am-8pm</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <img 
                src="https://www.hmsusa.org/img/logo.png" 
                alt="HMS Certified" 
                className="h-8 w-auto"
              />
            </div>
          </div>
          <div className="mt-6 pt-6 border-t border-[#DCD7CC] flex justify-between items-center">
            <p className="text-[#8A8A8A] text-sm">
              © 2024 Market Place on Oakton - Meat Department. HMS Certified Zabiha Halal.
            </p>
            <Link 
              to="/staff" 
              className="text-[#CECECE] text-xs hover:text-[#8A8A8A] transition-colors"
              data-testid="staff-link"
            >
              Staff
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
